import type { Block, Links } from "../lib/content";

/**
 * The renderer for a block list — shared by articles and quizzes.
 *
 * This used to live inside `ArticleBody`, which was fine while articles were
 * the only thing made of blocks. Quizzes are made of the same blocks (see
 * `lib/quiz/types.ts`), and the alternative to sharing this was a second copy
 * of the verse card and the numbered list that would drift within a month.
 *
 * Deliberately NOT `@tailwindcss/typography`: the legal pages use `prose`
 * because they are documents nobody reads twice, and the two things that decide
 * whether a piece of writing gets read — a measured line length and headings in
 * the brand's display face — are exactly what a generic prose reset flattens.
 */

/**
 * `{token}` or plain text, in one pass. Mirrors `LegalDocument`'s renderer.
 *
 * Hyphens are allowed inside a token because the token name IS the anchor text
 * — `{what-actually-holds}` renders as "what actually holds". That keeps the
 * link's words in the sentence where an editor can see them, and it makes
 * "click here" impossible to write by accident.
 */
const TOKEN = /(\{[a-zA-Z-]+\})/g;

export function Paragraph({ text, links }: { text: string; links?: Links }) {
  if (!links) return <p className="mt-5 leading-[1.75] text-body">{text}</p>;
  return (
    <p className="mt-5 leading-[1.75] text-body">
      {text.split(TOKEN).map((part, i) => {
        const name =
          part.startsWith("{") && part.endsWith("}") ? part.slice(1, -1) : null;
        const href = name ? links[name] : undefined;
        // Index keys: prose is static data whose order never changes at
        // runtime, and the same fragment recurs across paragraphs.
        // biome-ignore lint/suspicious/noArrayIndexKey: static, ordered prose
        if (!href) return <span key={i}>{part}</span>;
        return (
          <a
            // biome-ignore lint/suspicious/noArrayIndexKey: static, ordered prose
            key={i}
            href={href}
            className="font-semibold text-brand underline decoration-hair underline-offset-4"
          >
            {/* The visible words come from the token name, hyphens to spaces —
                so the anchor text is written in the sentence rather than being
                a generic "click here", which is the anchor text that tells a
                search engine nothing. */}
            {name?.replace(/-/g, " ")}
          </a>
        );
      })}
    </p>
  );
}

export function renderBlock(block: Block, i: number) {
  switch (block.type) {
    case "h2":
      return (
        <h2
          key={block.id}
          id={block.id}
          // `scroll-mt` so an anchor from the contents list does not land the
          // heading underneath the sticky header.
          className="mt-12 scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
        >
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3
          key={`h3-${i}`}
          className="mt-8 font-display text-xl font-bold text-ink"
        >
          {block.text}
        </h3>
      );
    case "p":
      return <Paragraph key={`p-${i}`} text={block.text} links={block.links} />;
    case "ul":
      return (
        <ul key={`ul-${i}`} className="mt-5 flex flex-col gap-3">
          {block.items.map((item) => (
            <li key={item} className="flex gap-3 leading-[1.75] text-body">
              <span
                aria-hidden="true"
                className="mt-2.5 size-2 shrink-0 rounded-pill bg-brand"
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={`ol-${i}`} className="mt-5 flex flex-col gap-4">
          {block.items.map((item, n) => (
            <li key={item} className="flex gap-3.5 leading-[1.75] text-body">
              <span
                aria-hidden="true"
                className="inline-flex size-7 shrink-0 items-center justify-center rounded-pill border-[2.5px] border-ink bg-surface font-display text-sm font-bold text-ink"
              >
                {n + 1}
              </span>
              <span className="pt-0.5">{item}</span>
            </li>
          ))}
        </ol>
      );
    case "verse":
      // Navy, never cobalt: scripture is not an action (docs/DESIGN-SYSTEM.md
      // §9.2). A left rule rather than a card, so a run of several verses reads
      // as a passage rather than as a stack of boxes.
      return (
        <figure
          key={`verse-${i}`}
          className="mt-7 border-l-[3px] border-scripture bg-scripture-wash/60 py-4 pr-4 pl-5"
        >
          <blockquote className="font-display text-[clamp(1.05rem,2.5vw,1.25rem)] leading-snug text-scripture">
            “{block.verse.text}”
          </blockquote>
          <figcaption className="mt-2 text-sm font-bold tracking-wide text-scripture uppercase">
            {block.verse.ref}
          </figcaption>
        </figure>
      );
    case "note":
      return (
        <aside
          key={`note-${i}`}
          className="brutal mt-7 bg-inset p-5 font-display text-lg leading-snug font-bold text-ink"
        >
          {block.text}
        </aside>
      );
  }
}

/** A contents list for any block list. Only worth it when there is somewhere to jump to. */
export function Contents({
  headings,
}: {
  headings: { id: string; text: string }[];
}) {
  if (headings.length < 3) return null;
  return (
    <nav aria-label="on this page" className="brutal mt-8 p-5 sm:p-6">
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        on this page
      </p>
      <ol className="mt-3 flex flex-col gap-2">
        {headings.map((heading) => (
          <li key={heading.id}>
            <a
              href={`#${heading.id}`}
              className="leading-snug text-body underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** The short-answer block: takeaways, above everything. */
export function ShortAnswer({ takeaways }: { takeaways: string[] }) {
  return (
    <section
      aria-label="the short answer"
      className="brutal mt-8 bg-inset p-6 sm:p-7"
    >
      <h2 className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        the short answer
      </h2>
      <ul className="mt-4 flex flex-col gap-3">
        {takeaways.map((line) => (
          <li key={line} className="flex gap-3 leading-[1.7] text-ink">
            <span
              aria-hidden="true"
              className="mt-2.5 size-2 shrink-0 rounded-pill bg-brand"
            />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The `<details>` FAQ, which is also what `faqJsonLd` marks up. */
export function FaqList({ items }: { items: { q: string; a: string }[] }) {
  return (
    <section className="mt-14">
      <h2 className="font-display text-2xl font-bold text-ink">Questions</h2>
      <div className="mt-5 flex flex-col gap-4">
        {items.map((item) => (
          <details key={item.q} className="brutal p-5">
            <summary className="cursor-pointer list-none font-display text-lg font-bold text-ink [&::-webkit-details-marker]:hidden">
              {item.q}
            </summary>
            <p className="mt-3 leading-relaxed text-body">{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
